import * as XLSX from 'xlsx-js-style';
import api from '../../../auth/apiClient';
import { isAxiosError } from 'axios';
import { trackDownload } from '../../../activity';

interface MonitoringInspeksiHarianProps {
    selectedMonth: string;
    selectedEstate: number | null;
    setIsLoading: (loading: boolean) => void;
    toast: any;
}

const MonitoringInspeksiHarian = {
    export: async ({ selectedMonth, selectedEstate, setIsLoading, toast }: MonitoringInspeksiHarianProps) => {

        try {
            const params: any = { month: selectedMonth };
            if (selectedEstate) {
                params.estate_id = selectedEstate;
            }

            const response = await api.get('/inspeksi/report', { params });

            const columnOrder = [
                'LEVEL',
                'JABATAN',
                'NIP',
                'NAMA',
                'DIVISI',
                'TOTAL',
                ...Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'))
            ];

            // Kelompokkan berdasarkan estate
            const groupedByEstate = response.data.reduce((acc: any, item: any) => {
                if (!acc[item.estate]) acc[item.estate] = [];
                acc[item.estate].push(item);
                return acc;
            }, {});

            const excelData: any[] = [];

            Object.entries(groupedByEstate).forEach(([estate, items]) => {
                // Baris judul estate
                const estateRow: any = { LEVEL: `${estate}` };
                excelData.push(estateRow);

                (items as any[]).forEach((item: any) => {
                    const row: any = {};
                    row['LEVEL'] = item.job_level;
                    row['JABATAN'] = item.job_title;
                    row['NIP'] = item.nip;
                    row['NAMA'] = item.name;
                    row['DIVISI'] = item.division;
                    row['TOTAL'] = 0;

                    for (let day = 1; day <= 31; day++) {
                        const dayStr = String(day).padStart(2, '0');
                        const count = item.daily_counts[dayStr] || 0;
                        row[dayStr] = count === 0 ? '-' : count;
                        row['TOTAL'] += count;
                    }

                    excelData.push(row);
                });
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData, { header: columnOrder });

            // Inject missing header cells
            for (let c = 0; c < columnOrder.length; c++) {
                const cell = XLSX.utils.encode_cell({ r: 0, c });
                if (!ws[cell]) ws[cell] = { v: columnOrder[c] };
            }

            const rowCount = excelData.length + 1;
            const lastCol = XLSX.utils.encode_col(columnOrder.length - 1);

            ws['!rows'] = [];
            ws['!rows'][0] = { hpt: 30 };
            ws['!cols'] = [
                { wch: 12 }, { wch: 25 }, { wch: 25 }, { wch: 23 }, { wch: 23 }, { wch: 6 },
                ...Array(31).fill({ wch: 5 }),
            ];
            ws['!ref'] = `A1:${lastCol}${rowCount}`;

            const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

            // Header styling
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cell = XLSX.utils.encode_cell({ r: 0, c: C });
                if (!ws[cell]) ws[cell] = { v: '' };
                ws[cell].s = {
                    font: { bold: true, color: { rgb: "FFFFFF" } },
                    fill: { type: "pattern", pattern: "solid", fgColor: { rgb: "4A5A33" } },
                    alignment: { horizontal: "center", vertical: "center" },
                    border: {
                        top: { style: "thin", color: { rgb: "000000" } },
                        bottom: { style: "thin", color: { rgb: "000000" } },
                        left: { style: "thin", color: { rgb: "000000" } },
                        right: { style: "thin", color: { rgb: "000000" } }
                    }
                };
            }

            const totalColIndex = columnOrder.indexOf('TOTAL');

            // Styling untuk seluruh isi data
            for (let R = range.s.r + 1; R <= range.e.r; ++R) {
                const levelCell = XLSX.utils.encode_cell({ r: R, c: 0 });
                const levelValue = ws[levelCell]?.v;
                const estateNames = ['ESTATE LEMATANG MAKMUR', 'ESTATE MUSI MAKMUR', 'KANTOR BESAR', 'KANTOR JAKARTA', 'PLASMA ABAB MAKMUR 1', 'PLASMA ABAB MAKMUR 2', 'TRAKSI', 'PMKS', 'UNDEFINED'];
                const isEstateRow = typeof levelValue === 'string' && estateNames.some(name => levelValue === `${name}`);

                if (isEstateRow) {
                    if (!ws['!rows']) ws['!rows'] = [];
                    ws['!rows'][R] = { hpt: 30 };
                    // Baris judul estate
                    ws[levelCell].s = {
                        font: { bold: true, color: { rgb: "000000" }, sz: "14" },
                        alignment: { horizontal: "left", vertical: "left" }
                    };
                    continue; // Lewati styling lainnya untuk baris ini
                }

                // Total column styling (hijau bold)
                const totalCell = XLSX.utils.encode_cell({ r: R, c: totalColIndex });
                if (ws[totalCell]) {
                    ws[totalCell].s = {
                        font: { bold: true, color: { rgb: "FFFFFF" } },
                        fill: {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { rgb: "4A5A33" }
                        },
                        alignment: { horizontal: "right", vertical: "right" },
                        border: {
                            top: { style: "thin", color: { rgb: "000000" } },
                            bottom: { style: "thin", color: { rgb: "000000" } },
                            left: { style: "thin", color: { rgb: "000000" } },
                            right: { style: "thin", color: { rgb: "000000" } }
                        }
                    };
                }

                // Styling tiap cell lain
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const cell = XLSX.utils.encode_cell({ r: R, c: C });
                    if (!ws[cell]) continue;

                    const value = ws[cell].v;
                    const isDateColumn = C >= 6 && C <= 36;

                    if (isDateColumn) {
                        if (value === '-') {
                            ws[cell].s = {
                                font: { italic: true, color: { rgb: "808080" } },
                                alignment: { horizontal: "center" },
                                border: {
                                    top: { style: "thin", color: { rgb: "000000" } },
                                    bottom: { style: "thin", color: { rgb: "000000" } },
                                    left: { style: "thin", color: { rgb: "000000" } },
                                    right: { style: "thin", color: { rgb: "000000" } }
                                }
                            };
                        } else {
                            ws[cell].z = '#,##0';
                            ws[cell].s = {
                                alignment: { horizontal: "center", vertical: "center" },
                                border: {
                                    top: { style: "thin", color: { rgb: "000000" } },
                                    bottom: { style: "thin", color: { rgb: "000000" } },
                                    left: { style: "thin", color: { rgb: "000000" } },
                                    right: { style: "thin", color: { rgb: "000000" } }
                                }
                            };
                        }
                    } else {
                        // Kolom selain tanggal
                        ws[cell].s = {
                            alignment: { horizontal: "left", vertical: "left" },
                            border: {
                                top: { style: "thin", color: { rgb: "000000" } },
                                bottom: { style: "thin", color: { rgb: "000000" } },
                                left: { style: "thin", color: { rgb: "000000" } },
                                right: { style: "thin", color: { rgb: "000000" } }
                            }
                        };
                    }
                    if (C === 5) {
                        ws[cell].s = {
                            alignment: { horizontal: "right", vertical: "right" },
                            border: {
                                top: { style: "thin", color: { rgb: "000000" } },
                                bottom: { style: "thin", color: { rgb: "000000" } },
                                left: { style: "thin", color: { rgb: "000000" } },
                                right: { style: "thin", color: { rgb: "000000" } }
                            }
                        };
                        // validasi untuk cell = 0 fill warna kuning
                        if (value === 0) {
                            ws[cell].s.fill = {
                                patternType: "solid",
                                fgColor: { rgb: "FFFF00" }
                            };
                        }
                    }
                }
            }

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, `Laporan Inspeksi ${selectedMonth}`);

            // Generate Excel file
            XLSX.writeFile(wb, `Laporan Inspeksi ${selectedMonth}.xlsx`);
            trackDownload('Laporan Inspeksi', { month: selectedMonth });

            toast({
                title: 'Success',
                description: 'Laporan inspeksi berhasil diekspor',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            let description = 'Gagal mengekspor laporan inspeksi';

            if (isAxiosError(error) && error.response) {
                description = `Gagal mengekspor laporan: ${error.response.data?.message || error.message}`;
            } else if (error instanceof Error) {
                description = `Gagal mengekspor laporan: ${error.message}`;
            }

            toast({
                title: 'Error',
                description,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            throw error;
        }
    }
};

export default MonitoringInspeksiHarian;
